import { useState } from 'react';

import { ImageWithFallback } from '@/components/image-with-fallback';
import { TextLink } from '@/components/text-link';
import { SITE_BASE_PATH } from '@/configs/site';
import {
  AUTHORISED_DISTRIBUTORS,
  PARALLEL_IMPORTERS,
} from '@/constants/global';
import { joinSgcmMedia, joinSgcmMsSite } from '@/utils/url/join/public';

import { PriceListModal } from '../../pricelist-model/component';
import { createQueryString } from '../../utils';
import { dealerTypeConfig } from './configs';
import { ICON_SIZE_IN_PIXEL, TEXT_OF_PI_CONTENT } from './constants';
import styles from './styles.module.scss';
import type { Component, DealerType } from './types';

export const CarMakeInfoItem: Component = ({
  dealerInfo,
}) => {
  const [
    isPriceListModalOpen,
    setIsPriceListModalOpen,
  ] = useState(false);
  const {
    id,
    name,
    brand,
    phoneArray,
    type,
    priceList,
    isCommercial,
  } = dealerInfo;

  const isAdContent = type === AUTHORISED_DISTRIBUTORS;
  const hasPriceList = priceList.length > 0;
  const lastPhoneIndex = phoneArray.length - 1;
  const generateTextDealerType = (dealerType: DealerType) => (
    dealerTypeConfig!.find(
      ({ value }) => value === dealerType,
    )?.text);

  const itemFullName = isAdContent && isCommercial
    ? `${brand} Commercial ${generateTextDealerType(type)}`
    : `${brand} ${generateTextDealerType(type)}`;

  const formatPhone = (phone: string) => `${phone.slice(0, 4)} ${phone.slice(4)}`;

  const handlePriceListClick = () => {
    setIsPriceListModalOpen(true);
  };

  return (
    <div className={styles.containerRoot}>
      <PriceListModal
        dealerId={id}
        isOpen={isPriceListModalOpen}
        priceList={priceList}
        onClose={() => {
          setIsPriceListModalOpen(false);
        }} />
      <div className={styles.textCarMakeName}>
        {itemFullName}
      </div>
      {isAdContent
        ? (
          <div className={styles.containerAdContent}>
            <div className={styles.containerLeftColumn}>
              <div>
                <TextLink
                  className={styles.textDealerName}
                  href={`${joinSgcmMsSite(SITE_BASE_PATH ?? '')}/listing/search?${createQueryString({
                    query: {
                      dealerId: id,
                      q: brand ?? undefined,
                      category: 'ad',
                    },
                  })}`}
                  text={name} />
              </div>
              <div className={styles.containerDealerPhones}>
                <ImageWithFallback
                  alt="phone icon"
                  height={ICON_SIZE_IN_PIXEL}
                  src={`${joinSgcmMedia('/public/new-cars/icon/outlinePhone.svg')}`}
                  title="hotline"
                  width={ICON_SIZE_IN_PIXEL}
                  priority />
                <div className={styles.textPhones}>
                  {phoneArray.map((phone, index) => (
                    <div key={phone}>
                      <TextLink
                        href={`tel:${phone}`}
                        text={formatPhone(phone)} />
                      { index < lastPhoneIndex
                      && <span className={styles.textSeperator}>/</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {hasPriceList && !isCommercial && (
              <button
                className={styles.containerPricelist}
                onClick={handlePriceListClick}>
                <ImageWithFallback
                  alt="pricelist icon"
                  height={ICON_SIZE_IN_PIXEL}
                  src={`${joinSgcmMedia('/public/new-cars/icon/pricelist.svg')}`}
                  title="pricelist"
                  width={ICON_SIZE_IN_PIXEL}
                  priority />
                Pricelist
              </button>
            )}
          </div>
        )
        : (
          <div className={styles.containerPiContent}>
            {TEXT_OF_PI_CONTENT}
            <div>
              View only
              <TextLink
                className={styles.linkToPi}
                href={`search?${createQueryString({
                  query: {
                    q: brand ?? undefined,
                    // TODO: change to use dealer_type
                    // after category select is modified to category radio
                    category: PARALLEL_IMPORTERS,
                  },
                })}`}
                text={` parallel imported ${brand}`} />
            </div>
          </div>
        )}
    </div>
  );
};
